import { useAuth0 } from '@auth0/auth0-react';
import { useAtom } from 'jotai';
import { useCallback } from 'react';
import { useQuery } from 'react-query';
import { useLocalStorage } from 'react-use';

import useRegistration from './useRegistration';

import { clearPopupsSS } from '@/modules/dashboard/components/PopupImageVIdeo';
import {
  checkLoggedIn,
  isShouldCheckLoggedInNative,
  setShouldCheckLoggedInNative,
} from '@/services/auth0';
import {
  enableZondaAuth,
  zondaGetUserProfile,
  zondaLogout,
} from '@/services/zonda';
import {
  userDataAtom,
  userLoggedInViaOTPAtom,
  userLoggedInViaZondaAtom,
} from '@/store';
import { UserInitialData, Maybe } from '@/types';
import { isNativeApp, notifyNative } from '@/utils/nativeBridge';

/**
 * useAuth
 * <br/>
 * Determina si está logueado. Da herramientas para modificar ese estado de login.
 * <br/>
 * Puede estar logueado via:
 * - auth0
 * - prelogueado (app nativa android/iOS + OTP anterior)
 * - OTP (hashLogin)
 * <br/>
 * Interactúa con AuthenticatedWrapper:
 *  -> Authenticated / AuthenticatedOTP / AuthenticatedNativo.
 * Para completar los datos iniciales del usuario.
 */
const useAuth = () => {
  const checkLoggedInJava = isShouldCheckLoggedInNative();
  const [localStorageUser0, setLocalStorageUser0, removeLocalStorageUser0] =
    useLocalStorage<string | null>('auth0-user', null);

  const { handleResetRegisterData } = useRegistration();
  const [userState, setUserState] = useAtom(userDataAtom);
  const { logout, isAuthenticated, isLoading, ...rest } = useAuth0();
  const [stateLoginViaOTP, setStateLoginViaOTP] = useAtom(
    userLoggedInViaOTPAtom
  );
  const [stateLoginViaZonda, setStateLoginViaZonda] = useAtom(
    userLoggedInViaZondaAtom
  );

  const handleLogout = () => {
    clearPopupsSS();
    setShouldCheckLoggedInNative(false);
    // eslint-disable-next-line no-console
    console.log('logout-useAuth-handleLogout');
    notifyNative('logout');
    if (enableZondaAuth && zondaGetUserProfile()) {
      zondaLogout();
    } else {
      logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      });
    }
    handleResetRegisterData();
    removeLocalStorageUser0();
  };

  const setUserInitialData = (data: Maybe<UserInitialData>) => {
    setUserState(data);
  };

  const isNative = isNativeApp();
  const auth0Loaded = !isLoading;

  const tmpResult = {
    isLoading,
    isAuthenticated,
  };
  if (isNative && isAuthenticated) {
    // solo para poder debuggear la situación sin usar la app nativa
    // deslogueo sólo la parte de auth0, queda la sesión
    for (const key in localStorage) {
      localStorage.removeItem(key);
    }
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  }

  const fnCheckLoggedIn = useCallback(async () => await checkLoggedIn(), []);

  const shouldCheckLoggedInNative =
    auth0Loaded && !isAuthenticated && (isNative || checkLoggedInJava);
  // eslint-disable-next-line no-console
  // console.log(` shouldCheckLoggedInNative: ${shouldCheckLoggedInNative}`);
  const { data: dataIsLogged, isLoading: isLoadingRefreshInitialData } =
    useQuery(['checkLoggedIn'], fnCheckLoggedIn, {
      refetchOnWindowFocus: false,
      keepPreviousData: false,
      enabled: shouldCheckLoggedInNative,
      staleTime: 25 * 60 * 1000,
      cacheTime: 25 * 60 * 1000,
    });

  if (shouldCheckLoggedInNative) {
    tmpResult.isLoading = isLoadingRefreshInitialData;
    tmpResult.isAuthenticated = dataIsLogged?.estado || false;
    /* if (!isLoading && isAuthenticated) {
      setLocalStorageUser0(JSON.stringify(userFake));
    } */
  } else if (
    !tmpResult.isAuthenticated &&
    stateLoginViaOTP.isUsingLoginViaHash
  ) {
    tmpResult.isAuthenticated = stateLoginViaOTP.isLoggedInViaHash;
    tmpResult.isLoading = stateLoginViaOTP.isLoading;
  } else if (!tmpResult.isAuthenticated && stateLoginViaZonda.isUsingZonda) {
    tmpResult.isAuthenticated = stateLoginViaZonda.isLoggedInViaZonda;
    tmpResult.isLoading = stateLoginViaZonda.isLoading;
  }

  return {
    ...rest,
    ...tmpResult,
    logout: handleLogout,
    setUserInitialData,
    userInitialData: userState,
    setLocalStorageUser0,
    removeLocalStorageUser0,
    isLocalStorageUser: Boolean(localStorageUser0),
    setStateLoginViaOTP,
    setStateLoginViaZonda,
  };
};

export default useAuth;
